import { Injectable } from '@angular/core';
import { retryFailedCall } from '@common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { InformationPopupStore } from '.';
import { InformationPopupActions } from '..';
import { ErrorType, VitoguideBackendSettingsApi } from '../../../projects/client';
import { UserPageSettings } from './information-popup.state';

interface KeyValueStorageObject<T> {
  data: T;
}

@Injectable()
export class InformationPopupEffects {
  readonly PAGE_SETTINGS_KEY = 'InformationPopupSettings';

  setPageSettings$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InformationPopupActions.setPageSettings),
      switchMap(() => {
        return of(InformationPopupActions.savePageSettings());
      })
    );
  });

  savePageSettings$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InformationPopupActions.savePageSettings),
      concatLatestFrom(() => this.informationPopupStore.allPagesSettings$),
      switchMap(([_action, settings]) => {
        const payload: KeyValueStorageObject<UserPageSettings[]> = {
          data: settings,
        };

        return this.settingApi.setKeyValueStorage(this.PAGE_SETTINGS_KEY, payload).pipe(
          retryFailedCall(3),
          map(() => InformationPopupActions.savePageSettingsSuccess()),
          catchError((error) => of(InformationPopupActions.savePageSettingsError({ error })))
        );
      })
    );
  });

  loadSettings$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InformationPopupActions.loadSettings),
      switchMap(() =>
        this.settingApi.getKeyValueStorage(this.PAGE_SETTINGS_KEY).pipe(
          retryFailedCall(3),
          map((response: KeyValueStorageObject<UserPageSettings[]>) => {
            return InformationPopupActions.loadSettingsSuccess({ settings: response.data });
          }),
          catchError((error) => {
            const resourceNotFound = error.errorType === ErrorType.RESOURCE_NOT_FOUND;
            if (resourceNotFound) {
              this.store.dispatch(InformationPopupActions.savePageSettings());
            }

            return of(InformationPopupActions.loadSettingsError({ error }));
          })
        )
      )
    );
  });

  constructor(
    private store: Store,
    private informationPopupStore: InformationPopupStore,
    private actions$: Actions,
    private settingApi: VitoguideBackendSettingsApi
  ) {}
}
